import { Facet } from '../models/Facet';
import { IOnCurrentItemKeyChangedProps } from '../components/itemsNavigator/ItemsNavigator';
import { ResultTypeEnum } from '@ms/telemetry-manager/lib/events/ResultTypeEnum';
import { IQosStartSchema, IQosEndSchema } from '@ms/telemetry-manager/lib/events/Qos.event';
import { IEngagementSingleSchema } from '@ms/telemetry-manager/lib/events/Engagement.event';
import { IEventContext } from '@ms/telemetry-manager/lib/IEvent';
import { KeyFacet } from '../models/KeyFacet';
import { Signal } from '../utilities/async/Signal';

/**
 * @public
 */
export const pageLoadConfigurationFacet = new Facet<{
    enablePageLoadBarrier?: boolean;
    pageLoadReadySignal?: Signal<void> // Signals that page load is ready
    pageLoadContinueSignal?: Signal<void>; // App resolves this to inform itemsscope to continue with page load
}>('pageLoadConfiguration');

/**
 * @internal
 */
export const hasPageLoadedFacet = new Facet<boolean>('hasPageLoaded');

/**
 * @internal
 */
export const resetPerfLoggerFacet =
    new Facet<(measurementInfo?: IMeasurementInfo) => void>('resetPerfLogger');

/**
 * @internal
 */
export const resetPerfLoggerKeyFacet = new KeyFacet<{}>('resetPerfLoggerKey', {});

/**
 * @internal
 */
export const internalPerfLoggerFacet =
    new Facet<(renderEndTime: number, measurementInfo?: IMeasurementInfo) => void>('internalPerfLogger');

/**
 * @internal
 */
export const internalPerfLoggerKeyFacet = new KeyFacet<{}>('internalPerfLoggerKey', {});

/**
 * @internal
 */
export interface IItemsViewRenderCompleteInfo {
    renderComplete?: boolean;
    keyChanges?: IOnCurrentItemKeyChangedProps;
}

/**
 * @internal
 */
export const itemsScopeInitTimeFacet = new Facet<number>('itemsScopeInitTime');
/**
 * @internal
 */
export const lastNavigationEventFacet = new Facet<IItemsViewRenderCompleteInfo>('lastNavigationEvent');

/**
 * To log events in the items-view code, add a loggerFacet
 * to the configuration.
 * @public
 */
export namespace ResultType {
    export const success = ResultTypeEnum.Success;
    export const failure = ResultTypeEnum.Failure;
    export const expectedFailure = ResultTypeEnum.ExpectedFailure;
}

/**
 * @public
 * @deprecated Use `IEngagementSingleSchema` from `@ms/telemetry-manager` instead.
 */
export { IEngagementSingleSchema as IEngagementData };

/**
 * @public
 * @deprecated Use `IQosStartSchema` from `@ms/telemetry-manager` instead.
 */
export { IQosStartSchema };

/**
 * @public
 * @deprecated Use `IQosEndSchema` from `@ms/telemetry-manager` instead.
 */
export { IQosEndSchema };

/**
 * Used by caller to get information about the transaction of this perf measurement
 * @internal
 */
export interface IMeasurementInfo {
    /**
     * Whether the number is for a full page load transition
     */
    isFullPageLoad: boolean;
    /**
     * Used by caller to extract page transition information of this measurement
     */
    keyChanges?: IOnCurrentItemKeyChangedProps;
    /**
     * identify which itemScope this measures, since there can be multiple scopes on a page
     */
    itemsScopeItemKey: string;
    /**
     * timestamp when item scope is initialized
     */
    itemsScopeInitTime?: number;
}

/**
 * Possible client ids for the sharing dialog. Copied from
 * /odsp-datasources/src/interfaces/sharing/enums/ClientId.ts
 * For any additions, keep that file in sync.
 * TODO: fix package dependencies to avoid duplication.
 * @internal
 */
export enum ClientId {
    unknown = 1,
    odb = 2,
    sharePoint = 3,
    ngsc = 4,
    word = 5,
    powerpoint = 6,
    excel = 7,
    onenoteWeb = 8,
    wac = 9,
    visio = 10,
    officecom = 11,
    listPart = 12, // SharePoint list web part
    outlookWeb = 13,
    odIos = 14,
    odAndroid = 15,
    teams = 16,
    sample = 17, // items-view demo code
    odc = 18,
    wordAndroid = 19,
    excelAndroid = 20,
    powerpointAndroid = 21,
    wordIos = 22,
    excelIos = 23,
    powerpointIos = 24,
    onenote = 25,
    teamsIos = 26,
    teamsAndroid = 27,
    onenoteIos = 28,
    visioIos = 29,
    onenoteAndroid = 30,
    visioAndroid = 31,
    outlookWindows = 32,
    outlookMac = 33,
    onenoteUWP = 34,
    officemobileIos = 35,
    officemobileAndroid = 36
}

/**
 * @public
 */
export interface ILogger {
    logEngagementData?: (payload: IEngagementSingleSchema, context: IEventContext) => void;

    /**
     * Will be called when the component is done rendering.
     * Used for RUMOne logging to calculate EUPL (End User Perceived Latency).
     * Note that renderEndTime will either be:
     * (1) milliseconds since page load began if browser supports performance.now()
     * (2) milliseconds since unix epoch aka Date.now() otherwise
     */
    logRenderEndTime?: (renderEndTime: number, measurementInfo?: IMeasurementInfo) => void;

    /**
     * Optional boolean indicates if logRenderEndTime will continue to be called
     * after initial page rendering with the same itemsScopeItemKey. Useful to log component render time
     * for in-page operations, like filtering, sorting, folder navigation, etc.
     * Default: false.
     */
    continueLoggingAfterPLT?: boolean;

    /**
     * Callback to start QoS event.
     * Returns callback to use upon ending the event.
     */
    logQoSData?: (startData: IQosStartSchema, context: IEventContext) => (endData: IQosEndSchema) => void;

    /**
     * Optional ScenarioId passed in by host to be used by RUMOne logger.
     */
    scenarioId?: string;

    /**
     * Optional clientId passed in by host to be used by SharingCallout.
     * Allows iframed sharing dialog to track usage by different clients.
     */
    sharingClientId?: ClientId;

    /**
     * Optional indicates if RUMOne data will be uploaded to data repository(Aria in current case)
     * Host app can pass in false to avoid logging debugging perf data, default true
     */
    reportRUMOnePerfData?: boolean;
}

/**
 * @public
 */
export const loggerFacet: Facet<ILogger> = new Facet<ILogger>('logger');
